import { type FunctionComponent } from 'react';
import { Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import cx from 'classnames';
import OperationSearchResults from './OperationSearchResults';
import JobsAndTripsSearchResults from './JobsAndTripsSearchResults';
import VesselSearchResults from './VesselSearchResults';
import { SearchContext } from '../../types';
import { useDetectMobileScreenWidth } from '../../../../hooks/useDetectMobileScreenWidth';
import { useSearchManager } from '../../hooks/useSearchManager';
import { translate, translateAndFormat } from '../../../../constants/translation';
import type { Page, TargetVessel } from '../../../../types';
import type { Timestamp } from '../../../../services/Timestamp';

interface Props {
  page: Page;
  operationKey: string;
  operationName: string;
  timestamp: Timestamp;
  searchPhrase: string;
  targetVessel: TargetVessel;
  context: SearchContext;
  onSearchClose: () => void;
  onVesselPositionReceived: (mmsi: number, operation: string, vesselName: string, timestamp?: Timestamp) => void;
  onOperationSelected: (operationKey: string) => void;
  onJobOrTripSelected: (operation: string, mmsi: string, timestamp: Timestamp, vesselName: string) => void;
  onSeeSomethingWrongOpen: () => void;
  onGlobalResultsRequested: (searchPhrase: string) => void;
}

const ResultsPane: FunctionComponent<Props> = ({
  page,
  operationKey,
  operationName,
  timestamp,
  searchPhrase,
  targetVessel,
  context,
  onSearchClose,
  onVesselPositionReceived,
  onOperationSelected,
  onJobOrTripSelected,
  onSeeSomethingWrongOpen,
  onGlobalResultsRequested,
}) => {
  const isMobileView = useDetectMobileScreenWidth();
  const {
    canSearch,
    vesselResults,
    operationResults,
    jobsAndTripsResults,
    isJobsAndTripsLoading,
    isVesselsLoading,
    isOperationsLoading,
  } = useSearchManager(operationKey, searchPhrase, targetVessel);

  const operationSearchResults = (
    <OperationSearchResults
      operationResults={operationResults}
      isLoading={isOperationsLoading}
      searchPhrase={searchPhrase}
      onSearchClose={onSearchClose}
      onOperationSelected={onOperationSelected}
    />
  );

  const jobsAndTripsSearchResults = (
    <JobsAndTripsSearchResults
      onSearchClose={onSearchClose}
      jobAndTripsResults={jobsAndTripsResults}
      isLoading={isJobsAndTripsLoading}
      onTripOrJobSelected={onJobOrTripSelected}
      onWhatWentWrongClick={onSeeSomethingWrongOpen}
      searchPhrase={searchPhrase}
      context={context}
      onGlobalResultsRequested={onGlobalResultsRequested}
    />
  );

  const vesselSearchResults = (
    <VesselSearchResults
      vesselResults={vesselResults}
      isLoading={isVesselsLoading}
      onSearchClose={onSearchClose}
      onVesselPositionReceived={onVesselPositionReceived}
      operationName={operationName}
      page={page}
      searchPhrase={searchPhrase}
      timestamp={timestamp}
      onWhatWentWrongClick={onSeeSomethingWrongOpen}
      context={context}
      onGlobalResultsRequested={onGlobalResultsRequested}
    />
  );

  const showOperationResults = context === SearchContext.Global;
  const showVesselResults = context !== SearchContext.Vessel;
  if (isMobileView) {
    return (
      <Tabs className={cx({ hidden: !canSearch })}>
        <TabList>
          {showOperationResults && (
            <Tab>
              <Text className="text-sm-medium">
                {translateAndFormat(
                  'SEARCH_RESULT_TAB_FORMAT',
                  translate('OPERATIONS'),
                  isOperationsLoading ? '' : operationResults.length.toString()
                )}
              </Text>
            </Tab>
          )}
          <Tab>
            <Text className="text-sm-medium">
              {translateAndFormat(
                'SEARCH_RESULT_TAB_FORMAT',
                translate('JOBS_AND_TRIPS'),
                isJobsAndTripsLoading ? '' : jobsAndTripsResults.length.toString()
              )}
            </Text>
          </Tab>
          {showVesselResults && (
            <Tab>
              <Text className="text-sm-medium">
                {translateAndFormat(
                  'SEARCH_RESULT_TAB_FORMAT',
                  translate('VESSELS'),
                  isVesselsLoading ? '' : vesselResults?.length.toString()
                )}
              </Text>
            </Tab>
          )}
        </TabList>

        <TabPanels className="search-results-container">
          {showOperationResults && <TabPanel>{operationSearchResults}</TabPanel>}
          <TabPanel>{jobsAndTripsSearchResults}</TabPanel>
          {showVesselResults && (
            <TabPanel>
              <>
                <Text className="message text-sm-regular">{translate('LAST_SEEN')}</Text>
                {vesselSearchResults}
              </>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    );
  }

  return (
    <div className={cx('search-results-container', 'results-scroll-bar', { hidden: !canSearch })}>
      {showOperationResults && operationSearchResults}
      {jobsAndTripsSearchResults}
      {showVesselResults && vesselSearchResults}
    </div>
  );
};

export default ResultsPane;
